import React, {useState} from 'react';
import {
    MdCallEnd,
    MdCheck,
    MdClose,
    MdContentCopy,
    MdExpandLess,
    MdExpandMore,
    MdMic,
    MdMicNone,
    MdMicOff,
    MdPlayArrow,
    MdRecordVoiceOver,
    MdTextSnippet
} from 'react-icons/md';
import {ImSpinner2} from 'react-icons/im';
import {SiMarkdown} from 'react-icons/si';
import {Input} from 'reactstrap';
import IconButton from '../components/ui/IconButton';
import VoiceActivityIndicator from './VoiceActivityIndicator';
import {VoiceActivityState} from './voiceActivity';
import {
    VoiceTranscriptFormat,
    VoiceTranscriptLine
} from './voiceTranscriptExport';

export type VoicePanelConnectionState =
    | 'idle'
    | 'connecting'
    | 'connected'
    | 'error';

/**
 * The voice widget's control panel, rendered two ways from the same state:
 * `compact` inside the desktop hover popover, and `touch` inside the phone
 * bottom sheet with targets sized for thumbs. All session logic stays in
 * VoiceAssistantControl; this component only lays out what it is given.
 */
export interface VoicePanelContentProps {
    layout: 'compact' | 'touch';
    idPrefix: string;
    status: string;
    /** The action running right now, or null when the widget is not working. */
    activity: VoiceActivityState | null;
    connectionState: VoicePanelConnectionState;
    /** Colour for the state dot; undefined when idle. */
    stateColor?: string;
    isMuted: boolean;
    idleSecondsRemaining: number | null;
    autoReadEnabled: boolean;
    selectedVoiceLabel: string;
    conversation: VoiceTranscriptLine[];
    errorDetails: unknown | null;
    errorCopied: boolean;
    isAudioPlaybackBlocked: boolean;
    onConnect: () => void;
    onToggleMute: () => void;
    onDisconnect: () => void;
    onPlayResponse: () => void;
    onOpenVoiceSettings: () => void;
    onSetAutoRead: (enabled: boolean) => void;
    onDownloadTranscript: (format: VoiceTranscriptFormat) => void;
    onCopyError: () => void;
    /** Touch layout only: closes the sheet. */
    onClose?: () => void;
    /** Provider-specific caution, e.g. the Azure quiet-environment note. */
    providerNote?: string | null;
}

// Clicking inside the desktop popover must not blur the microphone button,
// which would start the popover's hide timer. Harmless in the sheet.
const stopTooltipBlur = (event: React.MouseEvent) => event.preventDefault();

const clampedLineStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
};

const noPointer: React.CSSProperties = {pointerEvents: 'none'};

const speakerLabel = (role: VoiceTranscriptLine['role']) =>
    role === 'user' ? 'You' : 'Assistant';

const act = (handler: () => void) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handler();
};

const CompactPanel: React.FC<VoicePanelContentProps> = props => {
    const {
        idPrefix,
        status,
        activity,
        connectionState,
        stateColor,
        isMuted,
        idleSecondsRemaining,
        autoReadEnabled,
        selectedVoiceLabel,
        conversation,
        errorDetails,
        errorCopied,
        isAudioPlaybackBlocked
    } = props;
    const isConnected = connectionState === 'connected';

    return (
        <div className="text-start">
            <div className="d-flex align-items-center gap-2">
                <span
                    aria-hidden="true"
                    style={{
                        width: '0.6rem',
                        height: '0.6rem',
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: stateColor ?? 'var(--bs-secondary-color)'
                    }}
                />
                <span className="fw-semibold">Voice assistant</span>
            </div>
            <div className="small text-muted mt-1">{status}</div>
            {activity && (
                <VoiceActivityIndicator activity={activity} className="mt-1" />
            )}
            {props.providerNote && (
                <div className="small text-muted fst-italic mt-1">
                    {props.providerNote}
                </div>
            )}
            {idleSecondsRemaining !== null && (
                <div className="small text-warning fw-semibold">
                    Closing in {idleSecondsRemaining}s unless you speak.
                </div>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    title="Choose a voice"
                    onMouseDown={stopTooltipBlur}
                    onClick={act(props.onOpenVoiceSettings)}
                >
                    <MdRecordVoiceOver style={noPointer} />
                    {selectedVoiceLabel}
                </button>
                {isConnected && (
                    <button
                        type="button"
                        className={`btn btn-sm d-inline-flex align-items-center gap-1 ${
                            isMuted ? 'btn-warning' : 'btn-outline-secondary'
                        }`}
                        title={
                            isMuted
                                ? 'Unmute the microphone'
                                : 'Mute the microphone. The session stays open until you disconnect or it times out.'
                        }
                        aria-pressed={isMuted}
                        onMouseDown={stopTooltipBlur}
                        onClick={act(props.onToggleMute)}
                    >
                        {isMuted ? (
                            <MdMic style={noPointer} />
                        ) : (
                            <MdMicOff style={noPointer} />
                        )}
                        {isMuted ? 'Unmute' : 'Mute'}
                    </button>
                )}
                {isConnected && isAudioPlaybackBlocked && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                        onMouseDown={stopTooltipBlur}
                        onClick={act(props.onPlayResponse)}
                    >
                        <MdPlayArrow style={noPointer} />
                        Play response
                    </button>
                )}
                {isConnected && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 ms-auto"
                        title="End the chat"
                        onMouseDown={stopTooltipBlur}
                        onClick={act(props.onDisconnect)}
                    >
                        <MdCallEnd style={noPointer} />
                        Disconnect
                    </button>
                )}
            </div>

            <div className="form-check form-switch d-flex align-items-start gap-2 mt-2 mb-0 ps-0">
                <Input
                    type="switch"
                    id={`${idPrefix}-auto-read`}
                    className="ms-0 mt-1 flex-shrink-0"
                    role="switch"
                    checked={autoReadEnabled}
                    onMouseDown={stopTooltipBlur}
                    onChange={event =>
                        props.onSetAutoRead(event.target.checked)
                    }
                />
                <label
                    className="form-check-label mb-0"
                    htmlFor={`${idPrefix}-auto-read`}
                >
                    <span className="d-block">
                        Auto-read replies{' '}
                        <span className="text-muted">
                            ({autoReadEnabled ? 'on' : 'off'})
                        </span>
                    </span>
                    <span className="small text-muted d-block">
                        Read new replies aloud as they arrive. When off, the
                        assistant says when a reply is ready.
                    </span>
                </label>
            </div>

            {conversation.length > 0 && (
                <>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between align-items-center gap-2">
                        <span className="small fw-semibold">Transcript</span>
                        <div className="d-flex align-items-center gap-1">
                            <IconButton
                                type="button"
                                size={16}
                                className="btn btn-sm btn-outline-secondary p-1"
                                icon={<SiMarkdown style={noPointer} />}
                                title="Download as Markdown"
                                aria-label="Download transcript as Markdown"
                                onMouseDown={stopTooltipBlur}
                                onClick={act(() =>
                                    props.onDownloadTranscript('markdown')
                                )}
                            />
                            <IconButton
                                type="button"
                                size={16}
                                className="btn btn-sm btn-outline-secondary p-1"
                                icon={<MdTextSnippet style={noPointer} />}
                                title="Download as plain text"
                                aria-label="Download transcript as plain text"
                                onMouseDown={stopTooltipBlur}
                                onClick={act(() =>
                                    props.onDownloadTranscript('text')
                                )}
                            />
                        </div>
                    </div>
                    <div className="small mt-1">
                        {conversation.slice(-2).map((line, index) => (
                            <div
                                key={index}
                                className="mt-1"
                                style={clampedLineStyle}
                            >
                                <span className="fw-semibold">
                                    {speakerLabel(line.role)}:{' '}
                                </span>
                                {line.text}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {errorDetails !== null && errorDetails !== undefined && (
                <>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between align-items-center gap-2">
                        <span className="small fw-semibold text-danger">
                            Error details
                        </span>
                        <IconButton
                            type="button"
                            size={16}
                            className="btn btn-sm btn-outline-secondary p-1"
                            icon={
                                errorCopied ? (
                                    <MdCheck style={noPointer} />
                                ) : (
                                    <MdContentCopy style={noPointer} />
                                )
                            }
                            title={
                                errorCopied
                                    ? 'Error details copied'
                                    : 'Copy error details'
                            }
                            aria-label={
                                errorCopied
                                    ? 'Error details copied'
                                    : 'Copy error details'
                            }
                            onMouseDown={stopTooltipBlur}
                            onClick={act(props.onCopyError)}
                        />
                    </div>
                    <pre className="small mt-1 mb-0 text-wrap">
                        {JSON.stringify(errorDetails, null, 2)}
                    </pre>
                </>
            )}
        </div>
    );
};

type MicState = 'idle' | 'connecting' | 'listening' | 'muted' | 'error';

const getMicState = (
    connectionState: VoicePanelConnectionState,
    isMuted: boolean
): MicState =>
    connectionState === 'connecting'
        ? 'connecting'
        : connectionState === 'connected'
          ? isMuted
              ? 'muted'
              : 'listening'
          : connectionState === 'error'
            ? 'error'
            : 'idle';

const micLabel: Record<MicState, string> = {
    idle: 'Connect',
    connecting: 'Connecting',
    listening: 'Mute',
    muted: 'Unmute',
    error: 'Reconnect'
};

const micHint: Record<MicState, string> = {
    idle: 'Starts a voice chat. The session stays open until you disconnect or it times out.',
    connecting: 'Setting up the voice session.',
    listening:
        'Listening. The session stays open until you disconnect or it times out.',
    muted: 'Muted. The session stays open until you disconnect or it times out.',
    error: 'The last session failed. Tap to try again.'
};

const TouchPanel: React.FC<VoicePanelContentProps> = props => {
    const {
        idPrefix,
        status,
        activity,
        connectionState,
        stateColor,
        isMuted,
        idleSecondsRemaining,
        autoReadEnabled,
        selectedVoiceLabel,
        conversation,
        errorDetails,
        errorCopied,
        isAudioPlaybackBlocked
    } = props;
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const isConnected = connectionState === 'connected';
    const micState = getMicState(connectionState, isMuted);
    const onMicClick =
        micState === 'listening' || micState === 'muted'
            ? props.onToggleMute
            : props.onConnect;

    return (
        <div className="text-start">
            <div className="voice-sheet__handle" aria-hidden="true" />
            <div className="d-flex align-items-center gap-2">
                <span
                    aria-hidden="true"
                    className="voice-sheet__dot"
                    style={{
                        background: stateColor ?? 'var(--bs-secondary-color)'
                    }}
                />
                <span id={`${idPrefix}-sheet-title`} className="fw-semibold">
                    Voice assistant
                </span>
                {props.onClose && (
                    <IconButton
                        type="button"
                        size={24}
                        className="btn btn-outline-secondary rounded-circle voice-sheet__close ms-auto p-0"
                        icon={<MdClose style={noPointer} />}
                        title="Close"
                        aria-label="Close voice controls"
                        onClick={act(props.onClose)}
                    />
                )}
            </div>
            <div className="text-muted mt-1">{status}</div>
            {activity && (
                <VoiceActivityIndicator activity={activity} className="mt-2" />
            )}
            {props.providerNote && (
                <div className="voice-sheet__note small mt-2" role="note">
                    {props.providerNote}
                </div>
            )}
            {idleSecondsRemaining !== null && (
                <div className="voice-sheet__idle-bar mt-2" role="status">
                    Closing in {idleSecondsRemaining}s unless you speak.
                </div>
            )}

            <div className="d-flex flex-column align-items-center my-3">
                <button
                    type="button"
                    className={`voice-sheet__mic voice-sheet__mic--${micState}`}
                    aria-label={micLabel[micState]}
                    aria-pressed={
                        micState === 'muted'
                            ? true
                            : micState === 'listening'
                              ? false
                              : undefined
                    }
                    disabled={micState === 'connecting'}
                    onClick={act(onMicClick)}
                >
                    {micState === 'connecting' ? (
                        <ImSpinner2
                            className="voice-assistant-control__spinner"
                            style={noPointer}
                        />
                    ) : micState === 'muted' ? (
                        <MdMicOff style={noPointer} />
                    ) : micState === 'listening' ? (
                        <MdMic style={noPointer} />
                    ) : (
                        <MdMicNone style={noPointer} />
                    )}
                </button>
                <div className="fw-semibold mt-2">{micLabel[micState]}</div>
                <div className="small text-muted text-center">
                    {micHint[micState]}
                </div>
            </div>

            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary voice-sheet__action d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={act(props.onOpenVoiceSettings)}
                >
                    <MdRecordVoiceOver style={noPointer} />
                    Voice: {selectedVoiceLabel}
                </button>
                {isConnected && (
                    <button
                        type="button"
                        className="btn btn-outline-danger voice-sheet__action d-inline-flex align-items-center justify-content-center gap-2"
                        onClick={act(props.onDisconnect)}
                    >
                        <MdCallEnd style={noPointer} />
                        Disconnect
                    </button>
                )}
            </div>
            {isConnected && isAudioPlaybackBlocked && (
                <button
                    type="button"
                    className="btn btn-primary voice-sheet__action w-100 mt-2 d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={act(props.onPlayResponse)}
                >
                    <MdPlayArrow style={noPointer} />
                    Play response
                </button>
            )}

            <label
                className="voice-sheet__row d-flex align-items-center gap-3 mt-3 mb-0"
                htmlFor={`${idPrefix}-sheet-auto-read`}
            >
                <span className="flex-grow-1">
                    <span className="d-block fw-semibold">
                        Auto-read replies
                    </span>
                    <span className="small text-muted d-block">
                        Read new replies aloud as they arrive. When off, the
                        assistant says when a reply is ready.
                    </span>
                </span>
                <span className="form-check form-switch m-0 p-0 flex-shrink-0">
                    <Input
                        type="switch"
                        id={`${idPrefix}-sheet-auto-read`}
                        className="voice-sheet__switch m-0"
                        role="switch"
                        checked={autoReadEnabled}
                        onChange={event =>
                            props.onSetAutoRead(event.target.checked)
                        }
                    />
                </span>
            </label>

            {conversation.length > 0 && (
                <>
                    <hr className="my-3" />
                    <div className="d-flex justify-content-between align-items-center gap-2">
                        <span className="fw-semibold">Transcript</span>
                        <div className="d-flex align-items-center gap-1">
                            <IconButton
                                type="button"
                                size={20}
                                className="btn btn-outline-secondary voice-sheet__icon-button"
                                icon={<SiMarkdown style={noPointer} />}
                                title="Download as Markdown"
                                aria-label="Download transcript as Markdown"
                                onClick={act(() =>
                                    props.onDownloadTranscript('markdown')
                                )}
                            />
                            <IconButton
                                type="button"
                                size={20}
                                className="btn btn-outline-secondary voice-sheet__icon-button"
                                icon={<MdTextSnippet style={noPointer} />}
                                title="Download as plain text"
                                aria-label="Download transcript as plain text"
                                onClick={act(() =>
                                    props.onDownloadTranscript('text')
                                )}
                            />
                        </div>
                    </div>
                    <div className="voice-sheet__transcript mt-2">
                        {conversation.slice(-4).map((line, index) => (
                            <div key={index} className="mb-2">
                                <span className="fw-semibold">
                                    {speakerLabel(line.role)}:{' '}
                                </span>
                                {line.text}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {errorDetails !== null && errorDetails !== undefined && (
                <>
                    <hr className="my-3" />
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-link text-danger voice-sheet__row d-inline-flex align-items-center gap-1 p-0 text-decoration-none flex-grow-1 justify-content-start"
                            aria-expanded={showErrorDetails}
                            onClick={act(() =>
                                setShowErrorDetails(current => !current)
                            )}
                        >
                            {showErrorDetails ? (
                                <MdExpandLess style={noPointer} />
                            ) : (
                                <MdExpandMore style={noPointer} />
                            )}
                            {showErrorDetails
                                ? 'Hide error details'
                                : 'Show error details'}
                        </button>
                        <IconButton
                            type="button"
                            size={20}
                            className="btn btn-outline-secondary voice-sheet__icon-button"
                            icon={
                                errorCopied ? (
                                    <MdCheck style={noPointer} />
                                ) : (
                                    <MdContentCopy style={noPointer} />
                                )
                            }
                            title={
                                errorCopied
                                    ? 'Error details copied'
                                    : 'Copy error details'
                            }
                            aria-label={
                                errorCopied
                                    ? 'Error details copied'
                                    : 'Copy error details'
                            }
                            onClick={act(props.onCopyError)}
                        />
                    </div>
                    {showErrorDetails && (
                        <pre className="small mt-2 mb-0 text-wrap">
                            {JSON.stringify(errorDetails, null, 2)}
                        </pre>
                    )}
                </>
            )}

            {props.onClose && (
                <button
                    type="button"
                    className="btn btn-outline-secondary voice-sheet__action w-100 mt-3 d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={act(props.onClose)}
                >
                    <MdClose style={noPointer} />
                    Close
                </button>
            )}
        </div>
    );
};

const VoicePanelContent: React.FC<VoicePanelContentProps> = props =>
    props.layout === 'touch' ? (
        <TouchPanel {...props} />
    ) : (
        <CompactPanel {...props} />
    );

export default VoicePanelContent;
